import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import * as mod from "../../url";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import Sidebar from "../Sidebar";
import Navbar from "../Navbar/Navbar";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { color } from "framer-motion";

const AllJobSeekerUsers = () => {
  const [jobSeekerusers, setJobSeekerusers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();
  const toast = useToast();
  const storedData = JSON.parse(sessionStorage.getItem("superAdminLawvs"));
  const token = storedData?.data?.token;
  const config = { headers: { Authorization: `${token}` } };
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const SIDEBAR_WIDTH = "250px";

  // Fetch all users
  const fetchUsers = async (currentPage = 1) => {
    try {
      const { data } = await axios.get(
        `${mod.api_url}/api/v1/user/get-all-users?page=${currentPage}&limit=${limit}`,
        config,
      );
      setJobSeekerusers(data?.data || []);
      setTotalPages(data.pagination?.pages || 1);
      setPage(data.pagination?.page || 1);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error fetching users.",
        description: error.response?.data?.message || "Something went wrong!",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page]);

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    try {
      await axios.put(
        `${mod.api_url}/api/v1/user/update/status/${id}`,

        { status: newStatus },
        config,
      );

      setJobSeekerusers((prev) =>
        prev.map((user) =>
          user._id === id ? { ...user, status: newStatus } : user,
        ),
      );

      toast({
        title: `User status updated to ${newStatus}.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to update status", error);
      toast({
        title: "Error updating user status.",
        description: error.response?.data?.message || "Something went wrong!",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Filter by search + date
  useEffect(() => {
    let filtered = [...jobSeekerusers];

    if (search.trim()) {
      filtered = filtered.filter(
        (u) =>
          u.fullName?.toLowerCase().includes(search.toLowerCase()) ||
          u.email?.toLowerCase().includes(search.toLowerCase()) ||
          u.phone?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter((u) => {
        const created = new Date(u.createdAt);
        return created >= start && created <= end;
      });
    }

    setFilteredUsers(filtered);
  }, [search, startDate, endDate, jobSeekerusers]);

  // Export filtered users to Excel
  const exportToExcel = () => {
    if (filteredUsers.length === 0) {
      toast({
        title: "No data to export.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredUsers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "JobSeekerUsers");
    XLSX.writeFile(workbook, "JobSeekerUsers.xlsx");
  };
  const handleViewDetails = (userId) => {
    navigate(`/user-details/${userId}`);
  };

  const handleDownloadResume = (resumeUrl, fullName) => {
    try {
      if (!resumeUrl) {
        toast({
          title: "Resume not available.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const link = document.createElement("a");
      link.href = resumeUrl;
      link.download = `${fullName || "resume"}.pdf`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } catch (error) {
      console.error("Error downloading resume:", error);
      toast({
        title: "Failed to download resume.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Sidebar />
      <Navbar />

      <Box
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
        mt="70px"
        p={5}
        bg="gray.50"
        minH="100vh"
      >
        <Container maxW="7xl">
          <Heading
            color="white"
            textAlign="center"
            fontSize="2xl"
            mb={6}
            bg="teal.600"
            p={4}
            borderRadius="md"
            boxShadow="md"
          >
            Job Seeker Users
          </Heading>

          {/* Filters */}
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={4}
            bg="white"
            p={4}
            borderRadius="lg"
            boxShadow="sm"
            mb={6}
          >
            <InputGroup w={{ base: "100%", md: "30%" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by name, email, or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                bg="gray.100"
                borderRadius="md"
              />
            </InputGroup>

            <Flex gap={3} align="center" w={{ base: "100%", md: "40%" }}>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                bg="gray.100"
                borderRadius="md"
              />
              <Text>to</Text>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                bg="gray.100"
                borderRadius="md"
              />
            </Flex>

            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="teal"
              onClick={exportToExcel}
              w={{ base: "100%", md: "auto" }}
            >
              Export Excel
            </Button>
          </Flex>

          {/* Table */}
          <Box
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            overflowX="auto"
            p={2}
          >
            <Text fontSize="sm" mb={2} textAlign="right" pr={3}>
              Total Records: {jobSeekerusers?.length}
            </Text>

            {jobSeekerusers?.length === 0 ? (
              <Text textAlign="center" p={8} color="gray.500">
                No job seeker users found.
              </Text>
            ) : (
              <Table variant="striped" colorScheme="gray">
                <Thead bg="teal.600">
                  <Tr>
                    <Th color="white">#</Th>
                    <Th color="white">Name</Th>
                    <Th color="white">Email</Th>
                    <Th color="white">Mobile</Th>
                    <Th color="white">Date</Th>
                    <Th color="white">Resume</Th>
                    <Th color="white">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.map((user, index) => (
                    <Tr key={user._id}>
                      <Td>{index + 1}</Td>
                      <Td
                        fontWeight="medium"
                        onClick={() => handleViewDetails(user?._id)}
                        cursor={"pointer"}
                        _hover={{ color: "blue", textDecoration: "underline" }}
                      >
                        {user?.fullName.toUpperCase()}
                      </Td>
                      <Td>{user?.email}</Td>
                      <Td>{user?.phone}</Td>
                      <Td>{new Date(user?.createdAt).toLocaleString()}</Td>
                      <Td>
                        {user.resumeUrl ? (
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() =>
                              handleDownloadResume(`${mod.api_url}${user.resumeUrl}`, user.fullName)
                            }
                          >
                            Download
                          </Button>
                        ) : (
                          <Text color="gray.400" fontSize="sm">
                            No Resume
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          color="white"
                          bg={
                            user?.status === "active" ? "green.500" : "red.500"
                          }
                          _hover={{
                            bg:
                              user?.status === "active"
                                ? "green.600"
                                : "red.600",
                          }}
                          onClick={() =>
                            handleStatusToggle(user?._id, user?.status)
                          }
                        >
                          {user?.status === "active" ? "Active" : "Inactive"}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>
        </Container>
        {totalPages > 1 && (
          <Flex justify="center" mt={6} gap={2} wrap="wrap">
            <Button
              size="sm"
              onClick={() => setPage((prev) => prev - 1)}
              isDisabled={page === 1}
            >
              Prev
            </Button>

            {Array.from({ length: totalPages }, (_, i) => (
              <Button
                key={i}
                size="sm"
                bg={page === i + 1 ? "blue.500" : "gray.200"}
                color={page === i + 1 ? "white" : "black"}
                _hover={{
                  bg: page === i + 1 ? "blue.500" : "gray.300",
                }}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </Button>
            ))}

            <Button
              size="sm"
              onClick={() => setPage((prev) => prev + 1)}
              isDisabled={page === totalPages}
            >
              Next
            </Button>
          </Flex>
        )}
      </Box>
    </>
  );
};

export default AllJobSeekerUsers;
